import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, MapPin, Eye, Calendar, Share2, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PropertyGallery } from '@/components/properties/PropertyGallery';
import { PropertyFeatures } from '@/components/properties/PropertyFeatures';
import { PropertyContactForm } from '@/components/properties/PropertyContactForm';
import { PropertyCard } from '@/components/properties/PropertyCard';
import { useProperty, useSimilarProperties } from '@/hooks/useProperties';
import { formatCurrency } from '@/lib/format';
import { PROPERTY_PURPOSES } from '@/types/property';
import { toast } from 'sonner';
import { SEO } from '@/components/seo/SEO';

export default function PropertyDetailsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: property, isLoading, error } = useProperty(slug || '');
  const { data: similarProperties } = useSimilarProperties(property);

  const handleShare = async () => {
    try {
      await navigator.share({
        title: property?.title,
        text: property?.description || `Confira este imóvel: ${property?.title}`,
        url: window.location.href,
      });
    } catch {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copiado para a área de transferência!');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="container-custom py-8">
          <Skeleton className="mb-6 h-8 w-48" />
          <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
            <div>
              <Skeleton className="aspect-[16/9] w-full rounded-lg" />
              <div className="mt-8 space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
            <Skeleton className="h-[500px] rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="container-custom py-20 text-center">
          <h1 className="mb-4 text-2xl font-bold text-foreground">Imóvel não encontrado</h1>
          <p className="mb-8 text-muted-foreground">
            O imóvel que você está procurando não existe ou foi removido.
          </p>
          <Button asChild>
            <Link to="/imoveis">Ver todos os imóveis</Link>
          </Button>
        </div>
      </div>
    );
  }

  const images = property.property_images || [];
  const seoTitle = `${property.title.slice(0, 55)}`.slice(0, 60);
  const seoDesc = (property.description || `${property.title} em ${property.neighborhood}, ${property.city}.`).replace(/\s+/g, ' ').trim();
  const seoImage = property.main_image_url || images[0]?.image_url || undefined;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={`/imoveis/${property.slug}`}
        image={seoImage}
        type="product"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: property.title,
          description: seoDesc.slice(0, 300),
          image: seoImage ? [seoImage] : undefined,
          offers: {
            '@type': 'Offer',
            price: property.price,
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            url: `https://www.guilhermecavalheiro.com.br/imoveis/${property.slug}`,
          },
        }}
      />
    <div className="min-h-screen bg-background pt-20">
      {/* Breadcrumb */}
      <div className="border-b border-border bg-muted/30">
        <div className="container-custom py-4">
          <Link 
            to="/imoveis" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Voltar para imóveis
          </Link>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
          {/* Main Content */}
          <div>
            {/* Gallery */}
            <PropertyGallery 
              images={images} 
              mainImage={property.main_image_url} 
              title={property.title} 
            />

            {/* Property Header */}
            <div className="mt-8">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="bg-primary/10 text-primary">
                  {PROPERTY_PURPOSES[property.purpose]}
                </Badge>
                {property.is_featured && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                    Em Destaque
                  </Badge>
                )}
                {property.is_new && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Novo
                  </Badge>
                )}
              </div>

              <h1 className="mb-2 text-2xl font-bold text-foreground md:text-3xl">
                {property.title}
              </h1>

              <div className="mb-4 flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{property.neighborhood}, {property.city}</span>
              </div>

              <div className="mb-6 flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  <span>{property.views_count} visualizações</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Publicado em {new Date(property.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>

              {/* Price (Mobile) */}
              <div className="mb-6 lg:hidden">
                <p className="text-3xl font-bold text-primary">
                  {formatCurrency(property.price)}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="mb-8 flex gap-3">
                <Button variant="outline" size="sm" className="gap-2" onClick={handleShare}>
                  <Share2 className="h-4 w-4" />
                  Compartilhar
                </Button>
                <Button variant="outline" size="sm" className="gap-2">
                  <Heart className="h-4 w-4" />
                  Salvar
                </Button>
              </div>

              {/* Description */}
              {property.description && (
                <div className="mb-8">
                  <h2 className="mb-4 text-lg font-bold text-foreground">Descrição</h2>
                  <div className="prose prose-sm max-w-none text-muted-foreground">
                    {property.description.split('\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              <PropertyFeatures property={property} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            {/* Price (Desktop) */}
            <div className="mb-6 hidden rounded-lg border border-border bg-card p-6 lg:block">
              <p className="text-sm text-muted-foreground">Valor</p>
              <p className="text-3xl font-bold text-primary">
                {formatCurrency(property.price)}
              </p>
              {property.condo_fee && (
                <p className="mt-2 text-sm text-muted-foreground">
                  + {formatCurrency(property.condo_fee)}/mês de condomínio
                </p>
              )}
            </div>

            {/* Contact Form */}
            <PropertyContactForm property={property} />
          </div>
        </div>

        {/* Similar Properties */}
        {similarProperties && similarProperties.length > 0 && (
          <section className="mt-16">
            <h2 className="mb-6 text-xl font-bold text-foreground">Imóveis Semelhantes</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {similarProperties.map((prop) => (
                <PropertyCard key={prop.id} property={prop} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
    </>
  );
}
